import Disclosure from "../utils/Disclosure.js";
import debounce from "lodash.debounce";
import isElementVisible from "../utils/domHelpers.js";

export default class SiteHeader {
    constructor(element) {
        this.el = element;
        this.body = document.querySelector("body");
        this.barEl = this.el.querySelector(".site-header__bar");
        this.quickNav = this.el.querySelector(".site-header__quick-nav");
        this.logo = this.el.querySelector(".site-header__logo");
        this.navigationEl = this.el.querySelector(".site-header__navigation");
        this.mainEl = this.el.querySelector(".site-header__main");
        this.mainMenuEl = this.el.querySelector(".site-header__main-menu");
        this.burgerEl = this.el.querySelector(".btn-hamburger");
        this.currentPageAnchorEls = this.el.querySelectorAll(
            '.current-menu-item > [href*="#"]',
        );
        this.searchEl = this.el.querySelector(".site-header__search");
        this.mainToggles = this.el.querySelectorAll(
            "[aria-controls='site-header-main']",
        );
        this.subMenuToggles = this.el.querySelectorAll(".js-sub-menu-toggler");
        this.subMenuEls = this.el.querySelectorAll(".menu__sub-menu");

        this.searchToggles = this.el.querySelectorAll(
            "[aria-controls='site-header-search']",
        );

        // Stores the sub-menu Disclosure instances and the parent menu item for hover triggering.
        this.subMenuDropdowns = {};

        this.init();
    }

    init() {
        this.setupSearchToggle();

        // ---------------------------------------------------------------------
        // Handle the toggler elements that will open and close the menu.
        // ---------------------------------------------------------------------
        if (this.mainToggles.length > 0) {
            this.mainToggles.forEach((toggle) => {
                toggle.addEventListener("click", () => {
                    this.toggleHeader();
                });
            });
        }

        // ---------------------------------------------------------------------
        // Handle anchor links in the same page. Close the mobile menu if it is open.
        // ---------------------------------------------------------------------
        if (this.currentPageAnchorEls.length > 0) {
            this.currentPageAnchorEls.forEach((link) => {
                link.addEventListener("click", () => {
                    this.closeHeader(true);
                });
            });
        }

        // ---------------------------------------------------------------------
        // Set up sub menu expanding/collapsing functionality using Disclosure.
        // ---------------------------------------------------------------------
        if (this.subMenuEls.length > 0) {
            this.subMenuEls.forEach((element) => {
                if (element instanceof Element) {
                    const disclosure = new Disclosure(element, {
                        // collapseOnFocusout: !this.isBurgerModeActive(),
                        animate: true,
                        collapseOnAncestorCollapse: true,
                        collapseOnOtherElementsExpand: this.subMenuEls,
                    });

                    const dropdown = {
                        element,
                        disclosure,
                        parent: element.closest(".menu-item"),
                    };

                    // Add hover listeners to dropdown parents
                    this.subMenuDropdowns[element.id] = dropdown;

                    // Only add hover actions to top level submenus
                    if (!disclosure.el.matches("[data-menu-depth='0']")) {
                        return;
                    }

                    disclosure.collapse();

                    // dropdown.parent.addEventListener("mouseenter", (e) =>
                    //     this.handleSubMenuParentEvent(e),
                    // );
                    // dropdown.parent.addEventListener("mouseleave", (e) =>
                    //     this.handleSubMenuParentEvent(e),
                    // );
                }
            });
        }

        document.addEventListener("keydown", (e) => {
            if (e.key !== "Escape") {
                return;
            }

            this.handleEscKeyEvent(e);
        });

        this.el.addEventListener("collapseend", (e) => {
            this.body.classList.remove("is-site-header-submenu-expanded");

            const parentItem = e.target.closest(".menu-item");

            if (parentItem) {
                parentItem.classList.remove("is-submenu-expanded");
            }
        });

        this.el.addEventListener("expandend", (e) => {
            this.body.classList.add("is-site-header-submenu-expanded");

            const parentItem = e.target.closest(".menu-item");

            if (parentItem) {
                parentItem.classList.add("is-submenu-expanded");
            }
        });
    }

    toggleElementVisibility() {
        const isExpanded = this.el.classList.contains("is-open");

        this.elementsToShow.forEach((el) => {
            el.style.display = isExpanded ? "block" : "none";
        });

        this.elementsToHide.forEach((el) => {
            el.style.display = isExpanded ? "none" : "block";
        });
    }

    getHeight() {
        const headerHeight = this.barEl.offsetHeight;

        return headerHeight;
    }

    handleEscKeyEvent(event) {
        if (this.el.classList.contains("is-open")) {
            this.closeHeader();
        }
    }

    // setHeight() {
    //     this.headerHeight = this.getHeight();

    //     // add the height of the admin bar
    //     const adminBarHeight =
    //         document.querySelector("#wpadminbar")?.offsetHeight ?? 0;
    //     let headerBottom = adminBarHeight;

    //     // add the height of the announcement banner
    //     // const announcementBannerHeight =
    //     //     document.querySelector(".announcement-banner")?.offsetHeight ?? 0;
    //     // document.documentElement.style.setProperty(
    //     //     "--announcement-banner--height",
    //     //     `${announcementBannerHeight}px`,
    //     // );
    //     // headerBottom += announcementBannerHeight;

    //     document.documentElement.style.setProperty(
    //         "--site-header--bottom",
    //         `${headerBottom}px`,
    //     );

    //     this.el.classList.add("site-header--positioned");
    // }

    toggleHeader() {
        if (this.el.classList.contains("is-open")) {
            this.closeHeader();
            // this.toggleElementVisibility();
        } else {
            this.openHeader();
            // this.toggleElementVisibility();
        }
    }

    openHeader() {
        this.closeSearch();

        let first = "";

        if (this.mainMenuEl) {
            const listItems = Array.from(this.mainMenuEl.children);

            listItems.forEach((li) => {
                const a = li.querySelector("a");

                SiteHeader.setTabIndex([a], 0);

                if (first === "") {
                    first = a;
                }
            });
        }

        this.el.classList.add("is-open");
        this.mainEl.removeAttribute("inert");

        // Trap focus
        document.querySelector(".page-wrap").setAttribute("inert", true);
        this.quickNav.setAttribute("inert", true);
        this.logo.setAttribute("inert", true);

        document.documentElement.classList.add("no-scroll");

        this.mainToggles.forEach((toggle) => {
            toggle.setAttribute("aria-expanded", "true");
        });

        SiteHeader.setTabIndex(this.mainToggles, 0);

        if (this.mainMenuEl) {
            first.focus();
            // TODO [NW] - Check that first item gets focused
        }
    }

    closeHeader(initial = false) {
        // close the menu
        this.el.classList.remove("is-open");
        this.mainEl.setAttribute("inert", true);
        document.documentElement.classList.remove("no-scroll");

        // Untrap focus
        document.querySelector(".page-wrap").removeAttribute("inert");
        this.quickNav.removeAttribute("inert");
        this.logo.removeAttribute("inert");

        if (this.isBurgerModeActive()) {
            this.mainToggles.forEach((toggle) => {
                toggle.setAttribute("aria-expanded", "false");
            });

            // make the items not tabbable // currently handled by visibility:hidden
            // if (this.navigationEl) {
            //     const elements = SiteHeader.getTabbableItems(this.navigationEl);
            //     SiteHeader.setTabIndex(elements, -1);
            // }

            if (initial !== true) {
                // Focus the burger
                this.burgerEl.focus();
            }
        }
    }

    setupSearchToggle() {
        // ---------------------------------------------------------------------
        // Set up our search input expanding/collapsing functionality using Disclosure.
        // ---------------------------------------------------------------------
        if (this.searchEl && this.searchToggles.length > 0) {
            this.searchDisclosure = new Disclosure(this.searchEl, {
                collapseOnFocusout: true,
                focusWithinOnExpand: true,
                setHiddenAttribute: false,
                on: {
                    expandbegin: () => {
                        this.closeHeader();

                        document.documentElement.classList.add("no-scroll");
                        this.el.classList.add("is-search-open");
                    },
                    collapseend: () => {
                        document.documentElement.classList.remove("no-scroll");
                        this.el.classList.remove("is-search-open");
                    },
                },
            });
        }
    }

    closeSearch() {
        if (this.searchDisclosure) {
            this.searchDisclosure.collapse();
        }
    }

    // updateSubMenuDropdowns() {
    //     if (!this.subMenuDropdowns.length) {
    //         return;
    //     }

    //     this.subMenuDropdowns.forEach((dropdown) => {
    //         dropdown.disclosure.updateConfig({
    //             collapseOnFocusout: !this.isBurgerModeActive(),
    //         });
    //     });
    // }

    /**
     * Handle submenu parent events for hover triggering when in desktop mode.
     **/
    handleSubMenuParentEvent(event) {
        // Ignore submenu parent events if burger mode is active
        if (this.isBurgerModeActive()) {
            return;
        }

        const disclosureEl = event.target.querySelector(".sub-menu-toggler");
        const { disclosure } = this.subMenuDropdowns[disclosureEl.id] ?? {};

        if (!(disclosure instanceof Disclosure)) {
            return;
        }

        if (event.type === "mouseleave" && disclosure.isExpanded()) {
            disclosure.collapse();
        } else if (event.type === "mouseenter" && !disclosure.isExpanded()) {
            disclosure.expand();
        }
    }

    static setTabIndex(elements, index) {
        elements.forEach((element) => {
            element.tabIndex = index;
        });
    }

    static getTabbableItems(parent) {
        return parent.querySelectorAll("a, button");
    }

    isBurgerModeActive() {
        return isElementVisible(this.burgerEl);
    }
}

document.addEventListener("DOMContentLoaded", () => {
    const element = document.querySelector(".site-header");

    if (element) {
        new SiteHeader(element);
    }
});
